import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { GoldButton } from "@/components/primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitContact } from "@/lib/api";
import { site } from "@/config/site";
import { serviceOptions, contactMethods } from "@/data/content";

const FIELD_CLASS =
  "border-fs-border bg-transparent text-fs-ink placeholder:text-fs-muted/50 focus-visible:ring-fs-gold focus-visible:ring-1 focus-visible:border-fs-gold/60 h-11";

const initial = {
  firstName: "",
  lastName: "",
  workEmail: "",
  phone: "",
  companyName: "",
  website: "",
  primaryService: "",
  monthlyRevenue: "",
  challenge: "",
  preferredContact: "",
  privacyConsent: false,
  smsConsent: false,
  company_website_hp: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required.";
    if (!form.lastName.trim()) e.lastName = "Last name is required.";
    if (!form.workEmail.trim()) e.workEmail = "Work email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.workEmail))
      e.workEmail = "Enter a valid email address.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    if (!form.companyName.trim()) e.companyName = "Company name is required.";
    if (!form.primaryService) e.primaryService = "Please select a service.";
    if (!form.challenge.trim()) e.challenge = "Please describe your main challenge.";
    if (!form.preferredContact) e.preferredContact = "Select a preferred contact method.";
    if (!form.privacyConsent) e.privacyConsent = "Privacy consent is required to submit.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitContact(form);
      if (res?.success) {
        setDone(true);
        toast.success("Enquiry received. We'll be in touch shortly.");
        setForm(initial);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("We couldn't submit your enquiry. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-page-hero"
        label="Contact"
        title="Let's build a business"
        accent="that runs better."
        subtitle="Tell us about your business and where you want to go next. A member of the Fortuna Strive team will respond to qualified enquiries directly."
      />

      <section className="border-t border-fs-border bg-fs-bg2 py-20 lg:py-28">
        <div className="fs-shell grid gap-14 lg:grid-cols-12">
          {/* Left — contact details */}
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="fs-h3">Get in touch</h2>
              <p className="mt-4 fs-body">
                We work with a limited number of businesses at a time to protect
                the quality of every engagement.
              </p>
              <ul className="mt-10 space-y-6">
                <li className="flex items-start gap-4">
                  <Mail className="mt-1 h-5 w-5 text-fs-gold" strokeWidth={1.4} />
                  <a
                    href={`mailto:${site.contactEmail}`}
                    className="text-fs-ink transition-colors hover:text-fs-gold"
                    data-testid="contact-email-link"
                  >
                    {site.contactEmail}
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <Phone className="mt-1 h-5 w-5 text-fs-gold" strokeWidth={1.4} />
                  <span className="text-fs-muted">{site.phone}</span>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-fs-gold" strokeWidth={1.4} />
                  <span className="text-sm leading-relaxed text-fs-muted">
                    {site.registeredOffice}
                  </span>
                </li>
              </ul>
            </Reveal>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              {done ? (
                <div
                  className="flex flex-col items-center justify-center border border-fs-gold/30 bg-fs-card p-14 text-center"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="h-12 w-12 text-fs-gold" strokeWidth={1.2} />
                  <h3 className="mt-6 font-serif text-3xl text-fs-ink">Thank you.</h3>
                  <p className="mt-3 max-w-md fs-body">
                    Your enquiry has been received. A member of the Fortuna Strive
                    team will be in touch shortly.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-8 text-sm text-fs-gold hover:underline"
                    data-testid="contact-reset"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  noValidate
                  className="border border-fs-border bg-fs-card p-8 lg:p-10"
                  data-testid="contact-form"
                >
                  {/* Honeypot (hidden from users) */}
                  <div className="hidden" aria-hidden="true">
                    <label>
                      Company website
                      <input
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.company_website_hp}
                        onChange={(e) => set("company_website_hp", e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="First Name" id="firstName" error={errors.firstName} required>
                      <Input
                        id="firstName"
                        data-testid="input-firstName"
                        className={FIELD_CLASS}
                        value={form.firstName}
                        onChange={(e) => set("firstName", e.target.value)}
                      />
                    </Field>
                    <Field label="Last Name" id="lastName" error={errors.lastName} required>
                      <Input
                        id="lastName"
                        data-testid="input-lastName"
                        className={FIELD_CLASS}
                        value={form.lastName}
                        onChange={(e) => set("lastName", e.target.value)}
                      />
                    </Field>
                    <Field label="Work Email" id="workEmail" error={errors.workEmail} required>
                      <Input
                        id="workEmail"
                        type="email"
                        data-testid="input-workEmail"
                        className={FIELD_CLASS}
                        value={form.workEmail}
                        onChange={(e) => set("workEmail", e.target.value)}
                      />
                    </Field>
                    <Field label="Phone Number" id="phone" error={errors.phone} required>
                      <Input
                        id="phone"
                        data-testid="input-phone"
                        className={FIELD_CLASS}
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                      />
                    </Field>
                    <Field label="Company Name" id="companyName" error={errors.companyName} required>
                      <Input
                        id="companyName"
                        data-testid="input-companyName"
                        className={FIELD_CLASS}
                        value={form.companyName}
                        onChange={(e) => set("companyName", e.target.value)}
                      />
                    </Field>
                    <Field label="Website" id="website" error={errors.website}>
                      <Input
                        id="website"
                        data-testid="input-website"
                        placeholder="https://"
                        className={FIELD_CLASS}
                        value={form.website}
                        onChange={(e) => set("website", e.target.value)}
                      />
                    </Field>
                    <Field label="Primary Service Needed" id="primaryService" error={errors.primaryService} required>
                      <Select value={form.primaryService} onValueChange={(v) => set("primaryService", v)}>
                        <SelectTrigger
                          id="primaryService"
                          data-testid="select-primaryService"
                          className={FIELD_CLASS}
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
                          {serviceOptions.map((o) => (
                            <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Approximate Monthly Revenue" id="monthlyRevenue" hint="Optional">
                      <Select value={form.monthlyRevenue} onValueChange={(v) => set("monthlyRevenue", v)}>
                        <SelectTrigger
                          id="monthlyRevenue"
                          data-testid="select-monthlyRevenue"
                          className={FIELD_CLASS}
                        >
                          <SelectValue placeholder="Prefer not to say" />
                        </SelectTrigger>
                        <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
                          {["Under $10k", "$10k–$50k", "$50k–$100k", "$100k+", "Prefer not to say"].map((o) => (
                            <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div className="mt-6">
                    <Field label="Main Business Challenge" id="challenge" error={errors.challenge} required>
                      <Textarea
                        id="challenge"
                        rows={4}
                        data-testid="input-challenge"
                        className="border-fs-border bg-transparent text-fs-ink placeholder:text-fs-muted/50 focus-visible:ring-fs-gold focus-visible:ring-1 focus-visible:border-fs-gold/60"
                        value={form.challenge}
                        onChange={(e) => set("challenge", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="mt-6 max-w-xs">
                    <Field label="Preferred Contact Method" id="preferredContact" error={errors.preferredContact} required>
                      <Select value={form.preferredContact} onValueChange={(v) => set("preferredContact", v)}>
                        <SelectTrigger
                          id="preferredContact"
                          data-testid="select-preferredContact"
                          className={FIELD_CLASS}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="border-fs-border bg-fs-card text-fs-ink">
                          {contactMethods.map((o) => (
                            <SelectItem key={o} value={o} className="focus:bg-fs-bg2 focus:text-fs-gold">
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  {/* Consent — kept strictly separate */}
                  <div className="mt-8 space-y-5 border-t border-fs-border pt-8">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="privacyConsent"
                        data-testid="checkbox-privacy"
                        checked={form.privacyConsent}
                        onCheckedChange={(v) => set("privacyConsent", v === true)}
                        className="mt-1 border-fs-border data-[state=checked]:border-fs-gold data-[state=checked]:bg-fs-gold data-[state=checked]:text-[#0a0a0a]"
                      />
                      <Label htmlFor="privacyConsent" className="text-sm font-normal leading-relaxed text-fs-muted">
                        I agree to the{" "}
                        <Link to="/privacy-policy" className="text-fs-gold underline-offset-4 hover:underline">
                          Privacy Policy
                        </Link>{" "}
                        and consent to Fortuna Strive processing my information to
                        respond to my enquiry. <span className="text-fs-gold">*</span>
                      </Label>
                    </div>
                    {errors.privacyConsent && (
                      <p className="text-sm text-red-400" role="alert" data-testid="error-privacy">
                        {errors.privacyConsent}
                      </p>
                    )}

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="smsConsent"
                        data-testid="checkbox-sms"
                        checked={form.smsConsent}
                        onCheckedChange={(v) => set("smsConsent", v === true)}
                        className="mt-1 border-fs-border data-[state=checked]:border-fs-gold data-[state=checked]:bg-fs-gold data-[state=checked]:text-[#0a0a0a]"
                      />
                      <Label htmlFor="smsConsent" className="text-xs font-normal leading-relaxed text-fs-muted/80">
                        By checking this box, I agree to receive calls and text
                        messages from Fortunastrive Services Private Limited regarding
                        my enquiry, appointments, and requested services. Message
                        frequency varies. Message and data rates may apply. Reply STOP
                        to unsubscribe and HELP for assistance. Consent is not a
                        condition of purchase. See our{" "}
                        <Link to="/privacy-policy" className="text-fs-gold hover:underline">Privacy Policy</Link>{" "}
                        and{" "}
                        <Link to="/sms-terms" className="text-fs-gold hover:underline">SMS Terms</Link>.
                      </Label>
                    </div>
                  </div>

                  <div className="mt-8">
                    <GoldButton
                      type="submit"
                      variant="solid"
                      testId="contact-submit"
                      className="w-full sm:w-auto"
                      disabled={submitting}
                    >
                      {submitting ? "Sending…" : "Send Enquiry"}
                    </GoldButton>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, error, required, hint, children }) {
  return (
    <div>
      <Label htmlFor={id} className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-fs-muted">
        {label}
        {required && <span className="text-fs-gold">*</span>}
        {hint && <span className="normal-case tracking-normal text-fs-muted/60">({hint})</span>}
      </Label>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-400" role="alert" data-testid={`error-${id}`}>
          {error}
        </p>
      )}
    </div>
  );
}
